import type { ChildrenProps, Newsletters } from '@snowball-tech/common/types'

import getNewsletterInfo, {
  getOrderedNewsletters,
} from '@snowball-tech/common/helpers/newsletters'
import useTranslations from '@snowball-tech/common/hooks/useTranslations'
import { Button } from '@snowball-tech/fractal/dist/src/components/Button'
import { Popover } from '@snowball-tech/fractal/dist/src/components/Popover'
import { ScrollArea } from '@snowball-tech/fractal/dist/src/components/ScrollArea'
import { Tag } from '@snowball-tech/fractal/dist/src/components/Tag'
import { cj } from '@snowball-tech/fractal/dist/src/styles/helpers'
import { UilQuestionCircle as QuestionIcon } from '@tooni/iconscout-unicons-react'

import type { KeyboardEvent } from 'react'

type Props = {
  newsletters: Newsletters
  selected: Array<string>
  onToggle: (newsletterUuid: string) => void
} & Partial<ChildrenProps>
export default function NewsletterTagsList({
  children,
  newsletters,
  onToggle,
  selected = [],
}: Props) {
  const $t = useTranslations()

  const handleKeyDown = (
    event: KeyboardEvent<HTMLDivElement>,
    newsletterUuid: string,
  ) => {
    if (event.key === 'Enter' || event.key === ' ') {
      onToggle(newsletterUuid)
    }
  }

  const filters = getOrderedNewsletters(newsletters).map((newsletter) => {
    const { hasDescription, nameWithIcon, simpleDescription } =
      getNewsletterInfo(newsletter, $t, {
        fullQualifiedUrl: false,
      })

    return (
      <Tag
        key={newsletter.uuid}
        className={cj(
          'flex cursor-pointer gap-1 whitespace-nowrap hover:border-normal',
          selected.includes(newsletter.uuid) ? 'bg-black text-light' : '',
        )}
        color={selected.includes(newsletter.uuid) ? 'pink' : 'white'}
        role="button"
        size="m"
        onClick={() => onToggle(newsletter.uuid)}
        onKeyDown={(event) => handleKeyDown(event, newsletter.uuid)}
      >
        {nameWithIcon}

        {hasDescription && (
          <Popover
            key={newsletter.uuid}
            toggleOnTriggerClick={false}
            toggleOnTriggerHover
            trigger={
              <Button
                className="size-2 max-h-2 min-h-2 min-w-2 max-w-2 text-grey-30 [&_svg]:size-2"
                icon={<QuestionIcon />}
                iconOnly
                label={$t('editions.list.filters.newsletter.description.label')}
                variant="text"
              />
            }
            triggerAsButton={false}
            width={300}
            withArrow={false}
          >
            {simpleDescription}
          </Popover>
        )}
      </Tag>
    )
  })

  return (
    <>
      <div className="hidden flex-row flex-wrap justify-center gap-3 md:flex">
        {children}
        {filters}
      </div>

      <div className="flex flex-row items-center gap-2 md:hidden">
        {children}

        <ScrollArea
          className="-mb-2 mt-half flex-1 pb-2"
          orientation="horizontal"
        >
          <div className="flex flex-row justify-center gap-2">{filters}</div>
        </ScrollArea>
      </div>
    </>
  )
}
